// src/components/Admin/SellerManagement/RefundInvoiceModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Label } from '../../../../components/ui/label';
import { Input } from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../../../components/ui/radio-group';
import { Alert, AlertDescription } from '../../../../components/ui/alert';
import { Loader2 } from 'lucide-react';
import { initiateRefund } from '../../../../services/AdminApis'; // Assuming apiServices is correctly structured
import { useToast } from '../../../../hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
// src/components/Admin/SellerManagement/RefundInvoiceModal.jsx

const REFUND_REASONS = [
    "Purchased by mistake",
    "Business Shutting Down",
    "No Leads",
    "Other"
];

const RefundInvoiceModal = ({ isOpen, onClose, invoice, onRefundSuccess }) => {
  const [refundType, setRefundType] = useState('full');
  const [coinsToRefund, setCoinsToRefund] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setRefundType('full');
      setCoinsToRefund('');
      setRefundReason('');
      setTicketId('');
      setNotes('');
      setError('');
      setIsSubmitting(false);
    }
  }, [isOpen, invoice]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!ticketId) {
      setError('A Ticket ID is required.');
      return;
    }
    if (!refundReason) {
      setError('Please select a reason for the refund.');
      return;
    }
    if (refundType === 'partial' && (!coinsToRefund || coinsToRefund <= 0)) {
      setError('Please enter a valid number of coins for a partial refund.');
      return;
    }
    if (refundType === 'partial' && coinsToRefund >= invoice.coins_purchased) {
        setError('Partial refund must be for fewer coins than originally purchased.');
        return;
    }

    setIsSubmitting(true);

    const payload = {
      invoice_id: invoice.invoice_id,
      refund_type: refundType.toLocaleUpperCase(),
      coins_to_refund: refundType === 'partial' ? parseInt(coinsToRefund, 10) : null,
      refund_reason: refundReason,
      refund_ticket: ticketId,
      notes,
    };

    try {
      await initiateRefund(payload);
      toast({
        title: "Refund Initiated",
        description: `Refund for invoice #${invoice.invoice_id} has been successfully initiated.`,
        variant: "success",
      });
      onRefundSuccess();
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'An unexpected error occurred.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Refund Invoice #{invoice.document_id}</DialogTitle>
          <DialogDescription>
            Initiate a refund for {invoice.seller_name}. Total coins: {invoice.coins_purchased}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
                <div className="space-y-2">
                    <Label htmlFor="ticketId">Ticket ID*</Label>
                    <Input id="ticketId" value={ticketId} onChange={(e) => setTicketId(e.target.value)} />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="refundReason">Reason*</Label>
                    <Select onValueChange={setRefundReason} value={refundReason}>
                        <SelectTrigger id="refundReason">
                        <SelectValue placeholder="Select a reason" />
                        </SelectTrigger>
                        <SelectContent>
                        {REFUND_REASONS.map(reason => (
                            <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                        ))}
                        </SelectContent>
                    </Select>
                </div>
                
                <div className="space-y-2">
                    <Label>Type</Label>
                    <RadioGroup defaultValue="full" className="flex items-center space-x-4" onValueChange={setRefundType} value={refundType}>
                        <div className="flex items-center space-x-2"><RadioGroupItem value="full" id="r-full" /><Label htmlFor="r-full">Full</Label></div>
                        <div className="flex items-center space-x-2"><RadioGroupItem value="partial" id="r-partial" /><Label htmlFor="r-partial">Partial</Label></div>
                    </RadioGroup>
                </div>

                {refundType === 'partial' && (
                <div className="space-y-2">
                    <Label htmlFor="coins">Coins to Refund</Label>
                    <Input id="coins" type="number" value={coinsToRefund} onChange={(e) => setCoinsToRefund(e.target.value)} placeholder={`Max ${invoice.coins_purchased - 1}`} max={invoice.coins_purchased - 1} />
                </div>
                )}

                <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional: Add more details here." />
                </div>

                {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            </div>
          
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="secondary">Cancel</Button></DialogClose>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Initiate Refund
              </Button>
            </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RefundInvoiceModal;
